import type { Root } from "./prototypeTypes";
import type { MediaKind, MountedPlayer } from "./mediaPlayerTypes";

export function pickContainer(root: Root, kind: MediaKind): HTMLElement {
  if (kind === "audio") {
    const audioBar = (root as Document | HTMLElement).querySelector?.<HTMLElement>("#webPlayer") ?? null;
    if (audioBar) return audioBar;
    const fallback = document.createElement("div");
    fallback.id = "deepmox-audio-player-host";
    Object.assign(fallback.style, { position: "fixed", bottom: "0", left: "0", right: "0", pointerEvents: "none", zIndex: "1" });
    document.body.appendChild(fallback);
    return fallback;
  }
  return (root as Document | HTMLElement).querySelector?.<HTMLElement>("#videoFullscreen .vf-chapters")
    ?? (root as Document | HTMLElement).querySelector?.<HTMLElement>("#videoFullscreen")
    ?? document.body;
}

let sharedAudioElement: HTMLAudioElement | null = null;

/** Test isolation: drop the shared audio element between test cases. */
export function resetSharedAudioElementForTests(): void {
	if (sharedAudioElement) {
		// Specs shadow instance props (paused/duration) with configurable own
		// descriptors; a fresh element per test used to discard them for free.
		delete (sharedAudioElement as unknown as { paused?: unknown }).paused;
		delete (sharedAudioElement as unknown as { duration?: unknown }).duration;
	}
	sharedAudioElement = null;
}

export function ensureElement(kind: MediaKind): HTMLVideoElement | HTMLAudioElement {
  if (kind === "audio") {
    // iOS WebKit only grants a gesture-free play() to an element the user has
    // already started by hand, so chapter auto-advance (and lock-screen next)
    // must swap sources on one long-lived element instead of minting a fresh
    // one per mount — a new element was rejected NotAllowedError within
    // milliseconds. Per-mount listeners stay inert across mounts because every
    // element listener bails out on the mountedFor identity guard.
    if (sharedAudioElement) return sharedAudioElement;
    const audio = document.createElement("audio");
    // This element is created only after an explicit Listen request.  Some
    // Android vendor browsers keep a metadata-preloaded, off-screen element at
    // HAVE_METADATA even after play(), so ask them to buffer playable data.
    audio.setAttribute("preload", "auto");
    audio.setAttribute("playsinline", "true");
    Object.assign(audio.style, { position: "absolute", left: "-9999px", width: "1px", height: "1px" });
    sharedAudioElement = audio;
    return audio;
  }
  const video = document.createElement("video");
  // Video is likewise mounted only for an explicit Watch request; preload
  // enough data to satisfy Chromium-family vendor players before play().
  video.setAttribute("preload", "auto");
  video.setAttribute("playsinline", "true");
  video.setAttribute("x5-video-player-type", "h5");
  video.setAttribute("controlsList", "nodownload noremoteplayback");
  Object.assign(video.style, { width: "100%", height: "100%", objectFit: "contain" });
  return video;
}

export function attachTo(container: HTMLElement, element: HTMLMediaElement) {
  if (element.parentElement !== container) container.appendChild(element);
}

export function disposeMounted(media: MountedPlayer | undefined) {
  if (!media) return;
  media.element.pause();
  media.element.removeAttribute("src");
  media.element.load();
  media.element.remove();
}
